/**
 * The deterministic annual projection loop (spec §6).
 *
 * Order of operations within each year (it matters and is easy to get wrong):
 *   1. Advance ages / liveness; apply one-time events (incl. account routing).
 *   2. Gross earned income per owner; plan contributions (capped at the 2026
 *      limits with catch-ups; they reduce cash flow, and pre-tax ones reduce
 *      taxable income).
 *   3. Social Security for anyone claimed (foundation: own benefit w/ COLA;
 *      spousal/survivor/earnings-test in phase 10).
 *   4. Pension and annuity income — J&S survivor elections, exclusion ratios.
 *   5. RMDs: lifetime (Uniform Table) + inherited (10-year / stretch rules).
 *   6. Total outflow: spending + debt service + prior-year tax liability.
 *   7. Shortfall between outflow and available cash.
 *   8. Withdrawal engine sources the shortfall.
 *   9. Roth conversions (manual schedule; explorer in phase 14).
 *  10. Full federal tax on the resulting income picture (+ flat state approx).
 *      Filing status drops to single once only one spouse survives.
 *  11. Growth on remaining balances; apply the planned contributions + match.
 *      Surplus cash sweeps into the first after-tax account.
 *  12. Record the snapshot.
 *
 * Balances are NOMINAL. `cumulativeInflationFactor` lets the UI convert.
 */
import type {
  Account, AssumptionSet, FilingStatus, OneTimeEvent, Plan, TaxFamily,
} from '../model/types';
import { ACCOUNT_META } from '../model/accountMeta';
import { CURRENT_YEAR } from '../model/defaults';
import { contributionLimits } from './data/contributionLimits';
import { computeFederalTax } from './tax/federal';
import { taxableSocialSecurity } from './tax/ssTaxability';
import { computeFica } from './tax/fica';
import { computeRmd, computeInheritedRmd } from './tax/rmd';
import { claimingMultiplier, fraMonths, COLA_2026 } from './data/socialSecurity';
import { earningsTestWithholding, piaFromEarnings, spousalMultiplier } from './ssBenefits';
import { planWithdrawals, TRADITIONAL_ORDER } from './withdrawals';
import { addConversion, layersFromAccount, type RothLayers } from './rothLayers';
import { amortizeYear } from './amortization';
import { computeHealthcare, computeLtc } from './healthcare';
import {
  carryingCosts, computeSale, netRentalIncome, ownedInYear, propertyValueInYear,
} from './realEstate';
import { federalTaxYear } from './data/federal';
import { computeStateTax } from './tax/state';
import { stateProfileFor } from './data/states';
import type { AccountYearFlow, YearSnapshot } from './snapshot';

export interface ProjectionResult {
  snapshots: YearSnapshot[];
  /** First year spending could not be fully funded, if any. */
  firstDepletionYear?: number;
  assumptionLabel: string;
  /** Years in which flexible spending cut discretionary (Monte Carlo trials). */
  flexCutYears: number;
}

export interface YearReturns {
  equity: number;
  bond: number;
  cash: number;
}

export interface ProjectionOptions {
  /** Per-year return overrides, indexed from the start year (Monte Carlo). */
  returnsByYear?: YearReturns[];
  /**
   * Flexible spending (spec §13/§14): after a negative-equity year, cut
   * discretionary spending by this fraction. Real retirees do this; rigid
   * spending understates success by ~10-15 points.
   */
  discretionaryCutFraction?: number;
}

export function activeAssumptions(plan: Plan): AssumptionSet {
  return (
    plan.assumptionProfiles.find((p) => p.label === plan.activeAssumptionLabel) ??
    plan.assumptionProfiles[0]!
  );
}

function blendedReturn(
  allocation: { equityPct: number; bondPct: number; cashPct: number } | undefined,
  a: AssumptionSet,
  override?: number,
): number {
  if (override != null) return override;
  if (!allocation) return a.returnsByAssetClass.bond; // conservative default
  const total = allocation.equityPct + allocation.bondPct + allocation.cashPct || 100;
  return (
    (allocation.equityPct / total) * a.returnsByAssetClass.equity +
    (allocation.bondPct / total) * a.returnsByAssetClass.bond +
    (allocation.cashPct / total) * a.returnsByAssetClass.cash
  );
}

/**
 * Roughly what a windfall will cost in tax, used to hold money back before the
 * proceeds are routed to accounts.
 *
 * This is deliberately an estimate: the real liability depends on the whole
 * year's income, which is not known at the point the deposit is made. It leans
 * on last year's realised marginal rate where there is one, and on the
 * supplemental-withholding rate otherwise — the same thing an employer does
 * with a bonus. Being approximately right here beats depositing the gross and
 * discovering the shortfall a year later.
 */
export function estimateEventTaxReserve(
  ev: OneTimeEvent,
  priorYear: YearSnapshot | undefined,
  stateRate: number,
): number {
  if (ev.amount <= 0 || ev.taxTreatment === 'tax_free') return 0;

  if (ev.taxTreatment === 'ordinary') {
    // 22% is the federal supplemental withholding rate on bonuses.
    const federal = priorYear ? Math.max(0.12, priorYear.federal.marginalOrdinaryRate) : 0.22;
    return ev.amount * Math.min(0.5, federal + stateRate);
  }

  const gain = Math.max(0, ev.amount - (ev.costBasis ?? 0));
  if (gain <= 0) return 0;
  // Large gains reach the 20% bracket and drag the 3.8% surtax with them.
  const federal = gain > 600_000 ? 0.2 : gain > 50_000 ? 0.15 : 0;
  const surtax = gain > 250_000 ? 0.038 : 0;
  return gain * Math.min(0.5, federal + surtax + stateRate);
}

interface PlannedContribution {
  accountId: string;
  employee: number;
  match: number;
  /** Employee portion that reduces this year's ordinary income. */
  deductible: number;
  /** Match forfeited by contributing below the match threshold, this year. */
  unclaimedMatch: number;
}

/**
 * Plan this year's contributions with the statutory caps applied per owner:
 *  - 401(k)/403(b) share one elective-deferral bucket; 457(b) has its OWN
 *    separate limit (a real advantage for public-sector savers).
 *  - Age 50+ catch-up; ages 60-63 use the larger SECURE 2.0 super catch-up.
 *  - IRA bucket (trad + Roth combined), HSA bucket (self-only limit assumed).
 *  - Total employee contributions can't exceed the owner's earned income.
 *  - Pre-tax employer-plan + trad IRA (assumed deductible) + HSA reduce
 *    ordinary income; Roth contributions don't.
 */
function planContributions(
  accounts: Account[],
  agesByOwner: Record<string, number>,
  aliveByOwner: Record<string, boolean>,
  earnedByOwner: Record<string, number>,
  retirementAgeByOwner: Record<string, number>,
  year: number,
): PlannedContribution[] {
  const limits = contributionLimits(year);
  const out: PlannedContribution[] = [];

  // bucket key -> remaining room, per owner
  const room: Record<string, number> = {};
  const earnedRoom: Record<string, number> = {};

  const bucketOf = (t: Account['type']): 'plan' | 'plan457' | 'ira' | 'hsa' | null => {
    switch (t) {
      case 'trad_401k': case 'trad_403b': case 'roth_401k': case 'roth_403b':
        return 'plan';
      case 'trad_457b': case 'roth_457b':
        return 'plan457';
      case 'trad_ira': case 'roth_ira':
        return 'ira';
      case 'hsa':
        return 'hsa';
      default:
        return null; // brokerage/529/etc.: no federal cap enforced here
    }
  };

  const bucketLimit = (bucket: string, age: number): number => {
    const e = limits.employer;
    const catchUp =
      age >= 60 && age <= 63 ? e.superCatchUp60to63 : age >= 50 ? e.catchUp50 : 0;
    switch (bucket) {
      case 'plan':
      case 'plan457':
        return e.electiveDeferral + catchUp;
      case 'ira':
        return limits.ira.limit + (age >= 50 ? limits.ira.catchUp50 : 0);
      case 'hsa':
        return limits.hsa.selfOnly + (age >= 55 ? limits.hsa.catchUp55 : 0);
      default:
        return Infinity;
    }
  };

  for (const acct of accounts) {
    const c = acct.contributions;
    if (!c) continue;
    const ownerId = acct.ownerId;
    const age = agesByOwner[ownerId];
    if (age == null || !aliveByOwner[ownerId]) continue;
    const retAge = retirementAgeByOwner[ownerId] ?? 65;
    const endAge = c.endAge ?? retAge;
    if (age >= retAge || age > endAge) continue;

    const earned = earnedByOwner[ownerId] ?? 0;
    if (earned <= 0) continue;
    earnedRoom[ownerId] ??= earned;

    const bucket = bucketOf(acct.type);
    let allowed = c.employee;
    if (bucket) {
      const key = `${ownerId}:${bucket}`;
      room[key] ??= bucketLimit(bucket, age);
      allowed = Math.min(allowed, room[key]!);
    }
    allowed = Math.max(0, Math.min(allowed, earnedRoom[ownerId]!));
    if (bucket) room[`${ownerId}:${bucket}`]! -= allowed;
    earnedRoom[ownerId]! -= allowed;

    // Employer match: matchRate on the matchable portion (the lesser of the
    // employee's contribution and percentOfSalary of THIS OWNER's earnings —
    // never the household's, which would penalize a two-earner couple).
    let match = 0;
    let unclaimedMatch = 0;
    if (c.employerMatch) {
      const m = c.employerMatch;
      const fullyMatchable = earned * (m.percentOfSalary / 100);
      const matchable = Math.min(allowed, fullyMatchable);
      match = Math.min(matchable * m.matchRate, m.cap);
      // What the same rules would have paid had contributions reached the
      // threshold — the honest "left on the table" figure.
      unclaimedMatch = Math.max(0, Math.min(fullyMatchable * m.matchRate, m.cap) - match);
    }
    match += c.employerNonElective ?? 0;

    const family = ACCOUNT_META[acct.type].taxFamily;
    const deductible = family === 'pretax' || family === 'hsa' ? allowed : 0;

    if (allowed > 0 || match > 0 || unclaimedMatch > 0) {
      out.push({ accountId: acct.id, employee: allowed, match, deductible, unclaimedMatch });
    }
  }
  return out;
}

export function runProjection(
  plan: Plan,
  startYear: number = CURRENT_YEAR,
  opts?: ProjectionOptions,
): ProjectionResult {
  const a = activeAssumptions(plan);
  const people = plan.household.people;
  let flexCutYears = 0;

  const horizonYear =
    plan.horizonYearOverride ??
    Math.max(...people.map((p) => p.birthYear + Math.max(p.lifeExpectancyAge, a.longevityAge)));

  // Mutable engine state
  const balances: Record<string, number> = {};
  const bases: Record<string, number> = {};
  /** Roth ordering-rule state per account: contributions and conversion
   *  vintages, each with its own 5-year clock. Earnings are derived. */
  const rothLayers: Record<string, RothLayers> = {};
  for (const acct of plan.accounts) {
    balances[acct.id] = acct.balance;
    bases[acct.id] = acct.costBasis ?? 0;
    if (ACCOUNT_META[acct.type].taxFamily === 'roth') {
      rothLayers[acct.id] = layersFromAccount(acct);
    }
  }
  const debtBalances: Record<string, number> = {};
  for (const d of plan.debts) debtBalances[d.id] = d.balance;

  let priorYearTax = 0;
  let priorStateTax = 0;
  /** Capital-loss carryforward (tax-loss harvesting), nominal dollars. */
  let lossCarryforward = 0;
  /** MAGI history: [0] = last year, [1] = two years ago. Pre-plan years are
   *  approximated with the first year's income once it is known. */
  const magiHistory: number[] = [];
  let inflationFactor = 1;
  const snapshots: YearSnapshot[] = [];
  let firstDepletionYear: number | undefined;

  // Social Security: annual PIA in today's dollars per person (from the SSA
  // statement, or computed from an earnings history via the bend points).
  const pia12: Record<string, number> = {};
  for (const p of people) {
    const monthly =
      p.socialSecurity.estimatedMonthlyAtFRA ??
      (p.socialSecurity.earningsHistory?.length
        ? piaFromEarnings(p.socialSecurity.earningsHistory)
        : 0);
    pia12[p.id] = monthly * 12;
  }
  /** Earnings-test state: months withheld accumulate and are credited back at
   *  FRA via recomputation (the withholding is not truly lost). */
  const ssState: Record<string, { effectiveClaimMonths: number; withheldAccum: number; recomputed: boolean }> = {};

  for (let year = startYear; year <= horizonYear; year++) {
    const yearsFromStart = year - startYear;
    if (yearsFromStart > 0) inflationFactor *= 1 + a.generalInflation;

    // ── 1. Ages, liveness, one-time events ────────────────────────────────
    const ages: Record<string, number> = {};
    const alive: Record<string, boolean> = {};
    for (const p of people) {
      const age = year - p.birthYear;
      ages[p.id] = age;
      alive[p.id] = age <= Math.max(p.lifeExpectancyAge, a.longevityAge);
    }
    const aliveCount = people.filter((p) => alive[p.id]).length;

    // Last year's realised state burden, as a stand-in for this year's rate
    // when reserving tax on a windfall.
    const priorSnapshot = snapshots[snapshots.length - 1];
    const stateRateHint =
      priorSnapshot && priorSnapshot.federal.agi > 0
        ? Math.min(0.13, Math.max(0, priorSnapshot.stateTax / priorSnapshot.federal.agi))
        : stateProfileFor(plan.household.stateOfResidence).fidelity === 'none'
          ? 0
          : 0.05;

    let oneTimeInflows = 0;
    let oneTimeOutflows = 0;
    let oneTimeOrdinary = 0;
    let oneTimeLtcg = 0;
    let oneTimeCashInflow = 0; // spendable this year (not routed to an account)
    for (const ev of plan.oneTimeEvents) {
      if (ev.year !== year) continue;
      if (ev.amount >= 0) {
        oneTimeInflows += ev.amount;
        if (ev.taxTreatment === 'ordinary') oneTimeOrdinary += ev.amount;
        if (ev.taxTreatment === 'ltcg') oneTimeLtcg += Math.max(0, ev.amount - (ev.costBasis ?? 0));
        // Route the inflow: explicit splits first (in order, capped at what
        // remains), then the remainder destination, then spendable cash.
        const deposit = (accountId: string | undefined, amt: number): number => {
          if (!accountId || amt <= 0 || balances[accountId] == null) return 0;
          const acct = plan.accounts.find((x) => x.id === accountId)!;
          balances[accountId] = (balances[accountId] ?? 0) + amt;
          if (ACCOUNT_META[acct.type].taxFamily === 'aftertax') {
            bases[accountId] = (bases[accountId] ?? 0) + amt;
          }
          return amt;
        };
        // Hold back the estimated tax first, so what reaches the accounts is
        // net. Without this the plan banks the gross and then has to sell
        // something next spring to pay a bill it already knew about.
        const reserve = ev.reserveTaxFromProceeds
          ? estimateEventTaxReserve(ev, priorSnapshot, stateRateHint)
          : 0;
        let remainingInflow = Math.max(0, ev.amount - reserve);
        for (const split of ev.destinations ?? []) {
          remainingInflow -= deposit(
            split.accountId,
            Math.min(Math.max(0, split.amount), remainingInflow),
          );
        }
        remainingInflow -= deposit(ev.destinationAccountId, remainingInflow);
        // The reserve stays liquid, ready for next year's tax payment.
        oneTimeCashInflow += remainingInflow + reserve;
      } else {
        oneTimeOutflows += -ev.amount;
      }
    }

    // ── 1b. Property purchases, sales, carrying costs, rentals ────────────
    let propertyCosts = 0;
    let propertyTaxTotal = 0;
    let propertyRentalNet = 0;
    let propertyPurchaseOutflow = 0;
    let propertySaleNetProceeds = 0;
    let propertySaleLtcg = 0;
    let recaptureTaxEstimate = 0;
    let realEstateValue = 0;

    for (const prop of plan.properties) {
      // Future purchase: the price leaves the portfolio in the purchase year.
      if (prop.purchaseYear != null && prop.purchaseYear === year && prop.purchaseYear > startYear) {
        propertyPurchaseOutflow += propertyValueInYear(prop, year, startYear, a.housingAppreciation);
      }

      if (!ownedInYear(prop, year)) continue;
      const value = propertyValueInYear(prop, year, startYear, a.housingAppreciation);

      const costs = carryingCosts(prop, value, inflationFactor);
      propertyCosts += costs.total;
      propertyTaxTotal += costs.propertyTax;
      const rentalNet = netRentalIncome(prop, inflationFactor);
      propertyRentalNet += rentalNet;

      if (prop.plannedSaleYear === year) {
        // A future purchase resets the basis to the purchase-year price.
        const effectiveBasis =
          prop.purchaseYear != null && prop.purchaseYear > startYear
            ? propertyValueInYear(prop, prop.purchaseYear, startYear, a.housingAppreciation)
            : prop.costBasis;
        const sale = computeSale(
          { ...prop, costBasis: effectiveBasis }, value, year, startYear,
          plan.household.filingStatus,
        );
        let net = sale.amountRealized;
        // Pay off the linked mortgage from the proceeds.
        if (prop.mortgageId && (debtBalances[prop.mortgageId] ?? 0) > 0) {
          net -= debtBalances[prop.mortgageId]!;
          debtBalances[prop.mortgageId] = 0;
        }
        propertySaleLtcg += sale.taxableLtcgGain;
        recaptureTaxEstimate += sale.recaptureTaxEstimate;
        propertySaleNetProceeds += net;
        // Net proceeds land in cash (basis included — already taxed).
        const dest =
          plan.accounts.find((x) => x.type === 'checking_savings') ??
          plan.accounts.find((x) => ACCOUNT_META[x.type].taxFamily === 'aftertax');
        if (dest && net > 0) {
          balances[dest.id] = (balances[dest.id] ?? 0) + net;
          bases[dest.id] = (bases[dest.id] ?? 0) + net;
        }
      } else {
        // Owned and not sold this year: counts toward net worth.
        realEstateValue += value;
      }
    }

    // ── 2. Earned income per owner + contribution planning ────────────────
    const wageGrowthFactor = Math.pow(1 + a.wageGrowth, yearsFromStart);
    let wages = 0;
    let seIncome = 0;
    const earnedByOwner: Record<string, number> = {};

    for (const inc of plan.incomes) {
      const owner = people.find((p) => p.id === inc.ownerId);
      if (!owner || !alive[owner.id]) continue;
      const ownerAge = ages[owner.id]!;
      if (ownerAge < inc.startAge || ownerAge > inc.endAge) continue;
      if (inc.type === 'w2') {
        const amt = inc.annualAmount * wageGrowthFactor;
        wages += amt;
        earnedByOwner[owner.id] = (earnedByOwner[owner.id] ?? 0) + amt;
      } else if (inc.type === 'self_employment') {
        const amt = inc.annualAmount * wageGrowthFactor;
        seIncome += amt;
        earnedByOwner[owner.id] = (earnedByOwner[owner.id] ?? 0) + amt;
      }
    }

    const agesByOwner: Record<string, number> = {};
    const aliveByOwner: Record<string, boolean> = {};
    const retirementAgeByOwner: Record<string, number> = {};
    const separatedByOwner: Record<string, boolean> = {};
    /** Age at separation — what the Rule of 55 actually keys on. */
    const separationAgeByOwner: Record<string, number> = {};
    for (const p of people) {
      agesByOwner[p.id] = ages[p.id]!;
      aliveByOwner[p.id] = alive[p.id]!;
      retirementAgeByOwner[p.id] = p.retirementAge;
      separatedByOwner[p.id] = ages[p.id]! >= p.retirementAge;
      separationAgeByOwner[p.id] = p.retirementAge;
    }

    const planned = planContributions(
      plan.accounts, agesByOwner, aliveByOwner, earnedByOwner, retirementAgeByOwner, year,
    );
    const employeeContribTotal = planned.reduce((t, c) => t + c.employee, 0);
    const matchTotal = planned.reduce((t, c) => t + c.match, 0);
    const unclaimedMatchTotal = planned.reduce((t, c) => t + c.unclaimedMatch, 0);
    const deductibleContribTotal = planned.reduce((t, c) => t + c.deductible, 0);

    // ── 3. Social Security (own / spousal / survivor / earnings test) ─────
    // Benefits are computed fresh each year from PIA + claiming parameters:
    // today's-dollar amount → inflation-indexed to the claim year → COLA while
    // in payment. Spousal pays the GREATER of own or 50%-of-spouse's-PIA
    // (spousal-reduced, never delayed-credited); survivors step up to the
    // deceased's benefit including delayed credits.
    const claimAgeOf = (pid: string) =>
      plan.decisions.claimingAgeOverride?.[pid] ??
      people.find((x) => x.id === pid)!.socialSecurity.claimingAge;

    let ssGross = 0;
    for (const p of people) {
      if (!alive[p.id] || pia12[p.id]! <= 0) continue;
      const age = ages[p.id]!;
      const claimAge = claimAgeOf(p.id);
      if (age < claimAge) continue;

      const fraMo = fraMonths(p.birthYear);
      const fraAge = Math.ceil(fraMo / 12);
      const st = (ssState[p.id] ??= {
        effectiveClaimMonths: claimAge * 12,
        withheldAccum: 0,
        recomputed: false,
      });

      // FRA recomputation: months withheld by the earnings test are credited
      // back as if claiming had been delayed.
      if (!st.recomputed && age >= fraAge) {
        if (st.withheldAccum > 0) {
          st.effectiveClaimMonths = Math.min(70 * 12, st.effectiveClaimMonths + st.withheldAccum);
        }
        st.recomputed = true;
      }

      // Own benefit in today's dollars.
      const own = pia12[p.id]! * claimingMultiplier(st.effectiveClaimMonths, fraMo);

      // Spousal top: available once the other spouse (alive) has filed.
      let spousal = 0;
      const other = people.find((o) => o.id !== p.id);
      if (other && pia12[other.id]! > 0 && alive[other.id]) {
        const otherClaimed = (ages[other.id] ?? 0) >= claimAgeOf(other.id);
        if (otherClaimed) {
          spousal = 0.5 * pia12[other.id]! * spousalMultiplier(claimAge * 12, fraMo);
        }
      }

      let benefitToday = Math.max(own, spousal);

      // Survivor: the deceased's benefit (with any delayed credits they
      // earned) if greater than the survivor's own. Full-percentage survivor
      // (age-60 reduced claiming not modeled; deaths occur at/after FRA ages).
      if (other && !alive[other.id] && pia12[other.id]! > 0) {
        const otherFraMo = fraMonths(other.birthYear);
        const deathAge = Math.max(other.lifeExpectancyAge, a.longevityAge);
        const otherClaimAge = claimAgeOf(other.id);
        const deceasedMult =
          deathAge >= otherClaimAge
            ? claimingMultiplier(otherClaimAge * 12, otherFraMo)
            : 1; // died before claiming: survivor gets the PIA
        benefitToday = Math.max(benefitToday, pia12[other.id]! * deceasedMult);
      }

      // Index to nominal: inflation to the claim year, COLA while in payment.
      const claimYear = p.birthYear + claimAge;
      const indexToClaim = Math.pow(1 + a.generalInflation, Math.max(0, claimYear - startYear));
      const colaYears = Math.max(0, year - Math.max(claimYear, startYear));
      let nominal = benefitToday * indexToClaim * Math.pow(1 + COLA_2026, colaYears);

      // Earnings test before FRA (recomputed above once FRA arrives).
      if (age < fraAge && (earnedByOwner[p.id] ?? 0) > 0) {
        const et = earningsTestWithholding(
          nominal, earnedByOwner[p.id]!, false, inflationFactor,
        );
        nominal -= et.withheld;
        st.withheldAccum += et.withheldMonths;
      }

      // Policy haircut (pessimistic profile): labeled a policy assumption.
      if (a.socialSecurityHaircut && year >= a.socialSecurityHaircut.startYear) {
        nominal *= a.socialSecurityHaircut.payableFraction;
      }
      ssGross += nominal;
    }

    // ── 4. Pension / annuity / passive income (survivor-aware) ────────────
    let pensionIncome = 0;
    let annuityIncome = 0;
    let rentalRoyaltyOther = 0;
    let otherOrdinaryIncome = 0; // taxable portion of pensions/annuities/passive

    for (const inc of plan.incomes) {
      if (inc.type === 'w2' || inc.type === 'self_employment') continue;
      const owner = people.find((p) => p.id === inc.ownerId);
      if (!owner) continue;
      const ownerAge = ages[owner.id]!;
      if (ownerAge < inc.startAge) continue;

      const cola = Math.pow(1 + (inc.colaRate ?? 0), yearsFromStart);
      const isPension = inc.type.startsWith('pension');
      let amount = 0;

      if (alive[owner.id] && ownerAge <= inc.endAge) {
        amount = inc.annualAmount * cola;
        // J&S election reduces the benefit while the owner is alive.
        if (isPension && inc.survivorElection) {
          amount *= 1 - inc.survivorElection.benefitReduction;
        }
      } else if (!alive[owner.id] && isPension && inc.survivorElection && aliveCount > 0) {
        // Owner has died; the survivor share continues to the surviving spouse.
        const elected = inc.annualAmount * cola * (1 - inc.survivorElection.benefitReduction);
        amount = elected * (inc.survivorElection.percentToSurvivor / 100);
      }
      if (amount <= 0) continue;

      // Taxable portion.
      let taxable = amount;
      if (!inc.taxable || inc.taxTreatment === 'tax_free') {
        taxable = 0;
      } else if (inc.exclusionRatio != null) {
        // Non-qualified annuity: exclusionRatio is the untaxed return of basis.
        taxable = amount * (1 - inc.exclusionRatio);
      } else if (inc.taxTreatment === 'partially_taxable') {
        taxable = amount * 0.5; // generic default when no ratio given
      }

      if (isPension) pensionIncome += amount;
      else if (inc.type.startsWith('annuity')) annuityIncome += amount;
      else rentalRoyaltyOther += amount;
      otherOrdinaryIncome += taxable;
    }

    // ── 5. RMDs: lifetime + inherited ─────────────────────────────────────
    let totalRmd = 0;
    let rmdTaxable = 0;
    const rmdByAccount: Record<string, number> = {};
    for (const acct of plan.accounts) {
      const owner = people.find((p) => p.id === acct.ownerId);
      if (!owner || !alive[owner.id]) continue;
      if (acct.inherited) {
        const r = computeInheritedRmd(acct, balances[acct.id] ?? 0, owner.birthYear, year);
        if (r.amount > 0) {
          rmdByAccount[acct.id] = r.amount;
          totalRmd += r.amount;
          rmdTaxable += r.taxableAmount;
        }
      } else {
        const r = computeRmd(acct, balances[acct.id] ?? 0, owner.birthYear, ages[owner.id]!);
        if (r.amount > 0) {
          rmdByAccount[acct.id] = r.amount;
          totalRmd += r.amount;
          rmdTaxable += r.amount;
        }
      }
    }

    // ── 6. Spending ───────────────────────────────────────────────────────
    const anyoneWorking = people.some(
      (p) => alive[p.id] && ages[p.id]! < p.retirementAge,
    );
    const spending = computeSpending(
      plan, year, yearsFromStart, a.generalInflation, anyoneWorking, ages[people[0]!.id]!,
    );
    // Flexible spending: cut discretionary after a negative-equity year.
    if (opts?.discretionaryCutFraction && yearsFromStart > 0) {
      const prior = opts.returnsByYear?.[yearsFromStart - 1];
      if (prior && prior.equity < 0) {
        const cut = spending.discretionary * opts.discretionaryCutFraction;
        spending.discretionary -= cut;
        spending.total -= cut;
        flexCutYears += 1;
      }
    }

    // ── 6b. Healthcare + long-term care ───────────────────────────────────
    // A crude income proxy seeds the MAGI history in the first plan years.
    const grossIncomeProxy =
      wages + seIncome + pensionIncome + annuityIncome + rentalRoyaltyOther +
      propertyRentalNet + totalRmd + 0.85 * ssGross;
    const magiPrior = magiHistory[0] ?? grossIncomeProxy;
    const magiTwoBack = magiHistory[1] ?? magiHistory[0] ?? grossIncomeProxy;
    const hcInflationFactor = Math.pow(1 + a.healthcareInflation, yearsFromStart);

    // A plan with nothing entered yet is not a household that fails — it is a
    // blank form. Medicare's premiums are hard constants, so without this a
    // pristine plan would announce that the money runs out at 65.
    const nothingEntered =
      plan.accounts.length === 0 &&
      plan.incomes.length === 0 &&
      plan.properties.length === 0 &&
      plan.debts.length === 0 &&
      spending.total === 0;

    const healthcare = nothingEntered
      ? { total: 0, premiums: 0, oop: 0, acaSubsidyTotal: 0, fplRatio: null, irmaa: null, perPerson: [] }
      : computeHealthcare(
          plan, people, ages, alive, anyoneWorking, magiPrior, magiTwoBack, hcInflationFactor,
          inflationFactor,
        );
    const ltc = nothingEntered
      ? { cost: 0, insuranceOffset: 0, insurancePremiums: 0, net: 0 }
      : computeLtc(plan.ltcEvents, ages, alive, yearsFromStart, a.ltcInflation);

    let debtService = 0;
    let mortgageInterest = 0;
    for (const d of plan.debts) {
      const bal = debtBalances[d.id] ?? 0;
      if (bal <= 0) continue;
      const yr = amortizeYear(bal, d.annualRate, d.monthlyPayment + (d.extraPrincipalMonthly ?? 0));
      debtBalances[d.id] = yr.endBalance;
      debtService += yr.totalPaid; // payments stop at zero balance (spec §12)
      if (d.type === 'mortgage' || d.type === 'heloc') mortgageInterest += yr.interestPaid;
    }

    const totalOutflow =
      spending.total + healthcare.total + ltc.net +
      debtService + propertyCosts + propertyPurchaseOutflow +
      oneTimeOutflows + priorYearTax;

    // ── 7-8. Shortfall and withdrawals ────────────────────────────────────
    // RMDs come out regardless (a floor) and count as available cash.
    for (const [acctId, amt] of Object.entries(rmdByAccount)) {
      balances[acctId] = Math.max(0, (balances[acctId] ?? 0) - amt);
    }

    // Employee contributions are payroll deferrals — they reduce the cash
    // available for spending (the match never touches cash flow).
    const availableCash =
      wages + seIncome - employeeContribTotal +
      pensionIncome + annuityIncome + rentalRoyaltyOther + propertyRentalNet +
      ssGross + totalRmd + oneTimeCashInflow;

    // Filing status is needed here (bracket-aware room) and again at tax time:
    // a couple drops to single once only one spouse survives.
    const filingStatusUsed: FilingStatus =
      people.length === 2 && aliveCount === 1 &&
      (plan.household.filingStatus === 'mfj' || plan.household.filingStatus === 'qw')
        ? 'single'
        : plan.household.filingStatus;

    // Strategy: how much to withdraw this year (spec §7).
    const strategy = plan.decisions.withdrawalStrategy;
    const fixedPctActive = strategy === 'fixed_percentage' && !anyoneWorking;
    let shortfall: number;
    if (fixedPctActive) {
      // Withdraw X% of the portfolio regardless of need; spending flexes.
      const pct = plan.decisions.fixedPercentage ?? 0.04;
      const liquid = plan.accounts
        .filter((x) => ACCOUNT_META[x.type].taxFamily !== 'education')
        .reduce((t, x) => t + (balances[x.id] ?? 0), 0);
      // RMDs already distributed count toward the percentage.
      shortfall = Math.max(0, pct * (liquid + totalRmd) - totalRmd);
    } else {
      shortfall = Math.max(0, totalOutflow - availableCash);
    }

    const familyOrder: TaxFamily[] =
      plan.decisions.orderMode === 'custom' && plan.decisions.customOrder?.length
        ? plan.decisions.customOrder
        : TRADITIONAL_ORDER;

    // Bracket-aware ordering: how much ordinary-income room is left below the
    // top of the target bracket, given income already booked this year.
    let pretaxRoomCap: number | undefined;
    if (plan.decisions.orderMode === 'bracket_aware') {
      const fyNow = federalTaxYear(year, a.generalInflation);
      const ceiling = plan.decisions.bracketAwareCeiling ?? 0.22;
      const brs = fyNow.ordinaryBrackets[filingStatusUsed];
      const idx = brs.findIndex((b) => b.rate >= ceiling - 1e-9);
      const top = idx >= 0 && brs[idx + 1] ? brs[idx + 1]!.floor : Infinity;
      if (Number.isFinite(top)) {
        const eligible65 = people.filter((p) => alive[p.id] && ages[p.id]! >= 65).length;
        const deduction =
          fyNow.standardDeduction[filingStatusUsed] +
          eligible65 * fyNow.additional65Deduction[filingStatusUsed];
        const ordinarySoFar =
          wages + seIncome - deductibleContribTotal +
          otherOrdinaryIncome + propertyRentalNet + rmdTaxable;
        pretaxRoomCap = Math.max(0, top + deduction - ordinarySoFar);
      }
    }

    // Safety bucket (sequence-of-returns defense): drawn first after a
    // down-equity year, and optionally in retired pre-65 ACA years so low-gain
    // draws keep MAGI (and the subsidy) intact. In deterministic runs profile
    // returns are never negative, so the down-year trigger lives in Monte
    // Carlo and the stress test — where sequence risk actually bites.
    const bucketCfg = plan.decisions.bucketStrategy;
    // The bucket is only active during the sequence-risk window (the first N
    // years of retirement). Afterwards it stops refilling and gets spent like
    // any other account — holding a big cash pile forever costs more in drag
    // than a late crash would take.
    const firstRetirementYear = Math.min(
      ...people.map((p) => p.birthYear + p.retirementAge),
    );
    const bucketValid =
      bucketCfg &&
      balances[bucketCfg.accountId] != null &&
      year >= firstRetirementYear &&
      year <= firstRetirementYear + (bucketCfg.windowYears ?? 10);
    const priorEquityReturn =
      yearsFromStart > 0
        ? opts?.returnsByYear?.[yearsFromStart - 1]?.equity ?? a.returnsByAssetClass.equity
        : a.returnsByAssetClass.equity;
    let bucketDrawFirst = false;
    if (bucketValid) {
      const downYear = yearsFromStart > 0 && priorEquityReturn < 0;
      const acaProtection =
        (bucketCfg.protectAcaYears ?? true) &&
        !anyoneWorking &&
        (plan.healthcare?.preMedicareMode ?? 'aca') === 'aca' &&
        people.some(
          (p) => alive[p.id] && ages[p.id]! >= p.retirementAge && ages[p.id]! < 65,
        );
      bucketDrawFirst = downYear || acaProtection;
    }

    const wd = planWithdrawals(plan.accounts, balances, bases, {
      need: shortfall,
      familyOrder,
      year,
      agesByOwner,
      separatedByOwner,
      separationAgeByOwner,
      rothLayers,
      avoidEarlyPenalties: plan.decisions.avoidEarlyPenalties,
      pretaxRoomCap,
      bucket: bucketValid
        ? { accountId: bucketCfg.accountId, drawFirst: bucketDrawFirst }
        : undefined,
    });
    for (const w of wd.withdrawals) {
      balances[w.accountId] = Math.max(0, (balances[w.accountId] ?? 0) - w.amount);
      bases[w.accountId] = Math.max(0, (bases[w.accountId] ?? 0) - w.basisConsumed);
      if (w.rothLayersAfter) rothLayers[w.accountId] = w.rothLayersAfter;
    }

    // Under the fixed-percentage rule, spending flexes to what the rule
    // produced; under fund-spending, surplus sweeps into savings.
    let spendingEssential = spending.essential;
    let spendingDiscretionary = spending.discretionary;
    let spendingTotal = spending.total;
    let effectiveOutflow = totalOutflow;
    let unfunded = wd.unfundedNeed;
    let surplus = 0;

    if (fixedPctActive) {
      const bills = totalOutflow - spending.total; // healthcare, debt, taxes...
      const flexed = availableCash + wd.totalWithdrawn - bills;
      spendingTotal = Math.max(0, flexed);
      spendingEssential = Math.min(spendingEssential, spendingTotal);
      spendingDiscretionary = spendingTotal - spendingEssential;
      effectiveOutflow = bills + spendingTotal;
      unfunded = flexed < 0 ? -flexed : 0;
    } else {
      surplus = Math.max(0, availableCash - totalOutflow);
      if (surplus > 0) {
        const sweepTarget =
          plan.accounts.find((x) => x.type === 'checking_savings') ??
          plan.accounts.find((x) => ACCOUNT_META[x.type].taxFamily === 'aftertax');
        if (sweepTarget) {
          balances[sweepTarget.id] = (balances[sweepTarget.id] ?? 0) + surplus;
          bases[sweepTarget.id] = (bases[sweepTarget.id] ?? 0) + surplus;
        }
      }
    }

    const depleted = unfunded > 0.005;
    if (depleted && firstDepletionYear === undefined) firstDepletionYear = year;

    // Bucket refill: in up years (never in a draw-first year), top the bucket
    // back toward its inflation-indexed target from other after-tax accounts —
    // honestly realizing whatever gains the refill costs. Refills are GENTLE
    // (at most a third of the target per year): dumping the whole gap right
    // after a crash would sell half-recovered equities in bulk, which is the
    // exact mistake the bucket exists to avoid.
    let bucketRefillGains = 0;
    if (bucketValid && !bucketDrawFirst && priorEquityReturn >= 0) {
      const target = bucketCfg.targetToday * inflationFactor;
      const gap = Math.min(
        target - (balances[bucketCfg.accountId] ?? 0),
        target / 3,
      );
      if (gap > 500) {
        const refill = planWithdrawals(
          plan.accounts.filter((x) => x.id !== bucketCfg.accountId),
          balances,
          bases,
          { need: gap, familyOrder: ['aftertax'], year, agesByOwner, separatedByOwner },
        );
        for (const w of refill.withdrawals) {
          balances[w.accountId] = Math.max(0, (balances[w.accountId] ?? 0) - w.amount);
          bases[w.accountId] = Math.max(0, (bases[w.accountId] ?? 0) - w.basisConsumed);
        }
        if (refill.totalWithdrawn > 0) {
          balances[bucketCfg.accountId]! += refill.totalWithdrawn;
          bases[bucketCfg.accountId] =
            (bases[bucketCfg.accountId] ?? 0) + refill.totalWithdrawn;
          bucketRefillGains = refill.totalRealizedGains;
        }
      }
    }

    // ── 8b. Tax-loss harvesting: capture losses, reduce basis ─────────────
    // Down-year captures fire when this year's equity return is negative
    // (Monte Carlo / stress); the baseline captures lot-dispersion losses in
    // any year. Basis drops by every dollar harvested — future sales realize
    // correspondingly larger gains (deferral, honestly modeled).
    const tlh = plan.decisions.taxLossHarvesting;
    let tlhHarvested = 0;
    if (tlh?.enabled) {
      const thisYearEquity =
        opts?.returnsByYear?.[yearsFromStart]?.equity ?? a.returnsByAssetClass.equity;
      for (const acct of plan.accounts) {
        if (acct.type !== 'brokerage') continue;
        const bal = balances[acct.id] ?? 0;
        if (bal <= 0) continue;
        const equityShare = acct.allocation ? acct.allocation.equityPct / 100 : 0.6;
        let harvest = bal * (tlh.baselinePct ?? 0);
        if (thisYearEquity < 0) {
          harvest += bal * equityShare * -thisYearEquity * (tlh.downYearCapture ?? 0.75);
        }
        harvest = Math.min(harvest, bases[acct.id] ?? 0); // basis can't go negative
        if (harvest > 0) {
          bases[acct.id] = (bases[acct.id] ?? 0) - harvest;
          tlhHarvested += harvest;
        }
      }
    }

    // ── 9. Roth conversions (manual schedule and/or fill rule) ────────────
    let conversionOrdinary = 0;
    const executeConversion = (requested: number): void => {
      const pretax = plan.accounts
        .filter((x) => !x.inherited && ACCOUNT_META[x.type].taxFamily === 'pretax')
        .sort((x, y) => (balances[y.id] ?? 0) - (balances[x.id] ?? 0))[0];
      const roth = plan.accounts.find((x) => !x.inherited && ACCOUNT_META[x.type].taxFamily === 'roth');
      if (!pretax || !roth) return;
      const amount = Math.min(requested, balances[pretax.id] ?? 0);
      if (amount <= 0) return;
      balances[pretax.id]! -= amount;
      balances[roth.id] = (balances[roth.id] ?? 0) + amount;
      conversionOrdinary += amount;
      // Start this conversion's own 5-year penalty clock. Five years on, this
      // money is spendable penalty-free at ANY age — the conversion ladder.
      rothLayers[roth.id] = addConversion(
        rothLayers[roth.id] ?? layersFromAccount(roth),
        year,
        amount,
      );
    };

    for (const conv of plan.decisions.rothConversions ?? []) {
      if (conv.year === year) executeConversion(conv.amount);
    }

    const rule = plan.decisions.rothConversionRule;
    if (rule && year >= rule.startYear && year <= rule.endYear) {
      // Ordinary income already booked this year, before any conversion.
      const ordinaryBase = Math.max(
        0,
        wages + seIncome - deductibleContribTotal +
          otherOrdinaryIncome + propertyRentalNet + rmdTaxable +
          wd.totalOrdinaryIncome + oneTimeOrdinary,
      );
      let room = 0;
      if (rule.type === 'bracket') {
        const fyNow = federalTaxYear(year, a.generalInflation);
        const brs = fyNow.ordinaryBrackets[filingStatusUsed];
        const idx = brs.findIndex((b) => b.rate >= rule.target - 1e-9);
        const top = idx >= 0 && brs[idx + 1] ? brs[idx + 1]!.floor : undefined;
        if (top != null) {
          const eligible65Now = people.filter((p) => alive[p.id] && ages[p.id]! >= 65).length;
          const deduction =
            fyNow.standardDeduction[filingStatusUsed] +
            eligible65Now * fyNow.additional65Deduction[filingStatusUsed];
          // Taxable SS at this income level (the torpedo makes conversions
          // drag more SS into taxability — estimated here, exact at tax time).
          const ssTaxable = taxableSocialSecurity(
            year, filingStatusUsed, ordinaryBase + wd.totalRealizedGains, 0, ssGross,
          ).taxableBenefits;
          room = top + deduction - (ordinaryBase + ssTaxable);
        }
      } else {
        // IRMAA-threshold fill: stay just below the target MAGI. The target is
        // a today's-dollars threshold (IRMAA tiers index with CPI), so it grows
        // with inflation like the real tiers do.
        const magiEstimate = ordinaryBase + wd.totalRealizedGains + oneTimeLtcg + 0.85 * ssGross;
        room = rule.target * inflationFactor - 1_000 - magiEstimate; // $1k of cliff clearance
      }
      if (room > 500) executeConversion(room);
    }

    // ── 10. Taxes on this year's income picture ───────────────────────────
    // Net harvested losses (+ carryforward) against realized gains without
    // limit, then up to $3,000 against ordinary income (statutory, unindexed);
    // the remainder carries forward.
    const grossLtcg =
      wd.totalRealizedGains + oneTimeLtcg + propertySaleLtcg + bucketRefillGains;
    let lossesAvailable = tlhHarvested + lossCarryforward;
    const gainsOffset = Math.min(grossLtcg, lossesAvailable);
    lossesAvailable -= gainsOffset;
    const ordinaryLossOffset = Math.min(3_000, lossesAvailable);
    lossesAvailable -= ordinaryLossOffset;
    const ltcg = grossLtcg - gainsOffset;

    const ordinaryIncome =
      wages + seIncome - deductibleContribTotal +
      otherOrdinaryIncome + propertyRentalNet + rmdTaxable +
      wd.totalOrdinaryIncome + conversionOrdinary + oneTimeOrdinary -
      ordinaryLossOffset;

    // Itemized deductions: mortgage interest + SALT (property tax + prior-year
    // state income tax, capped) + deductible budget categories. The engine
    // takes the greater of standard and itemized.
    const fy = federalTaxYear(year, a.generalInflation);
    const saltComponent = Math.min(
      fy.saltCap.cap[filingStatusUsed],
      propertyTaxTotal + priorStateTax,
    );
    const itemizedDeductions = mortgageInterest + saltComponent + spending.deductible;

    const fed = computeFederalTax({
      year,
      filingStatus: filingStatusUsed,
      ages: people.filter((p) => alive[p.id]).map((p) => ages[p.id]!),
      ordinaryIncome: Math.max(0, ordinaryIncome),
      qualifiedDividends: 0,
      longTermCapitalGains: ltcg,
      socialSecurityBenefits: ssGross,
      itemizedDeductions,
      netInvestmentIncome: ltcg,
      inflationRate: a.generalInflation,
    });
    const fica = computeFica(year, filingStatusUsed, wages, seIncome, a.generalInflation);

    // Mid-plan relocation: the state switches in the relocation year.
    let stateCode = plan.household.stateOfResidence;
    for (const r of plan.household.relocations) {
      if (year >= r.year) stateCode = r.toState;
    }
    const oldestAge = Math.max(
      ...people.filter((p) => alive[p.id]).map((p) => ages[p.id]!),
    );
    const stateResult = computeStateTax(stateProfileFor(stateCode), {
      year,
      filingStatus: filingStatusUsed,
      age: oldestAge,
      ordinaryIncome: Math.max(0, ordinaryIncome),
      retirementIncome: Math.max(0, otherOrdinaryIncome + rmdTaxable + wd.totalOrdinaryIncome),
      capitalGains: ltcg,
      federallyTaxableSS: fed.ss.taxableBenefits,
      customEffectiveRate: plan.household.customStateEffectiveRate,
    });
    const stateTax = stateResult.tax;

    const totalTaxLiability =
      fed.totalTax + fica.total + stateTax + wd.totalPenalties + recaptureTaxEstimate;

    // ── 11. Growth, then planned contributions ────────────────────────────
    const accountFlows: AccountYearFlow[] = [];
    for (const acct of plan.accounts) {
      const meta = ACCOUNT_META[acct.type];
      const startBalance =
        (balances[acct.id] ?? 0) +
        (rmdByAccount[acct.id] ?? 0) +
        (wd.withdrawals.find((w) => w.accountId === acct.id)?.amount ?? 0);

      const yearReturns = opts?.returnsByYear?.[yearsFromStart];
      const effAssumptions = yearReturns ? { ...a, returnsByAssetClass: yearReturns } : a;
      const r = blendedReturn(acct.allocation, effAssumptions, acct.expectedReturnOverride);
      const growth = (balances[acct.id] ?? 0) * r;
      balances[acct.id] = (balances[acct.id] ?? 0) + growth;
      // Growth in after-tax accounts raises unrealized gain; basis unchanged.

      const p = planned.find((c) => c.accountId === acct.id);
      const contributions = p?.employee ?? 0;
      const employerMatch = p?.match ?? 0;
      if (contributions > 0 || employerMatch > 0) {
        balances[acct.id]! += contributions + employerMatch;
        if (meta.taxFamily === 'aftertax') {
          bases[acct.id] = (bases[acct.id] ?? 0) + contributions;
        }
        if (meta.taxFamily === 'roth' && contributions > 0) {
          // Direct Roth contributions build withdrawable basis — the employer
          // match does not (it lands pre-tax and is earnings for this purpose).
          const layers = rothLayers[acct.id];
          if (layers) layers.contributions += contributions;
        }
      }

      const withdrawal =
        (rmdByAccount[acct.id] ?? 0) +
        (wd.withdrawals.find((w) => w.accountId === acct.id)?.amount ?? 0);

      accountFlows.push({
        accountId: acct.id,
        label: acct.label,
        taxFamily: meta.taxFamily,
        startBalance,
        contributions,
        employerMatch,
        growth,
        withdrawal,
        rmdAmount: rmdByAccount[acct.id] ?? 0,
        endBalance: balances[acct.id] ?? 0,
        penalty: wd.withdrawals
          .filter((w) => w.accountId === acct.id)
          .reduce((t, w) => t + w.penalty, 0),
      });
    }

    const balancesByFamily: Record<TaxFamily, number> = {
      pretax: 0, roth: 0, aftertax: 0, hsa: 0, education: 0,
    };
    for (const acct of plan.accounts) {
      balancesByFamily[ACCOUNT_META[acct.type].taxFamily] += balances[acct.id] ?? 0;
    }
    const totalDebt = Object.values(debtBalances).reduce((t, b) => t + b, 0);
    const netWorth =
      Object.values(balances).reduce((t, b) => t + b, 0) + realEstateValue - totalDebt;

    // ── 12. Record ────────────────────────────────────────────────────────
    snapshots.push({
      year,
      ages,
      alive,
      filingStatusUsed,
      wagesIncome: wages,
      selfEmploymentIncome: seIncome,
      pensionIncome,
      annuityIncome,
      rentalRoyaltyOtherIncome: rentalRoyaltyOther + propertyRentalNet,
      socialSecurityGross: ssGross,
      oneTimeInflows,
      employeeContributions: employeeContribTotal,
      employerMatchTotal: matchTotal,
      employerMatchUnclaimed: unclaimedMatchTotal,
      surplusToSavings: surplus,
      // Budget-only figures; healthcare/LTC are separate outflow lines (§6)
      // and are added into must-spend by the UI's guaranteed-income view.
      // Under the fixed-percentage strategy these are the FLEXED amounts.
      essentialSpending: spendingEssential,
      discretionarySpending: spendingDiscretionary,
      totalSpending: spendingTotal,
      deductibleExpenses: spending.deductible,
      guaranteedIncome: ssGross + pensionIncome + annuityIncome,
      healthcarePremiums: healthcare.premiums,
      healthcareOOP: healthcare.oop,
      acaSubsidy: healthcare.acaSubsidyTotal,
      acaFplRatio: healthcare.fplRatio,
      irmaaTierIndex: healthcare.irmaa?.tierIndex ?? 0,
      ltcNetCost: ltc.net,
      realEstateValue,
      propertyCosts,
      mortgageInterest,
      propertyPurchaseOutflow,
      propertySaleNetProceeds,
      recaptureTaxEstimate,
      debtService,
      oneTimeOutflows,
      priorYearTaxPaid: priorYearTax,
      totalOutflow: effectiveOutflow,
      shortfallBeforeWithdrawals: shortfall,
      totalWithdrawals: wd.totalWithdrawn,
      realizedCapitalGains: wd.totalRealizedGains,
      earlyWithdrawalPenalty: wd.totalPenalties,
      depleted,
      unfundedSpending: unfunded,
      totalRmd,
      rothConversionAmount: conversionOrdinary,
      tlhHarvested,
      tlhCarryforward: lossesAvailable,
      federal: fed,
      fica,
      stateTax,
      totalTaxLiability,
      accounts: accountFlows,
      balancesByFamily,
      netWorth,
      cumulativeInflationFactor: inflationFactor,
    });

    priorYearTax = totalTaxLiability; // paid next year (spec §6 step 6)
    priorStateTax = stateTax; // SALT deduction input for next year
    lossCarryforward = lossesAvailable; // TLH carryforward into next year
    magiHistory.unshift(fed.agi); // [0] = last year, [1] = two back
    if (magiHistory.length > 2) magiHistory.pop();
  }

  return { snapshots, firstDepletionYear, assumptionLabel: a.label, flexCutYears };
}

/** Discretionary-spending multiplier from the age-banded (go-go / slow-go /
 *  no-go) schedule; keyed to the first person's age. 1 when no bands. */
function ageBandMultiplier(plan: Plan, primaryAge: number): number {
  const bands = plan.budget.ageBands;
  if (!bands?.length) return 1;
  let mult = 1;
  for (const band of [...bands].sort((a, b) => a.startAge - b.startAge)) {
    if (primaryAge >= band.startAge) mult = band.discretionaryMultiplier;
  }
  return mult;
}

export interface SpendingBreakdown {
  essential: number;
  discretionary: number;
  total: number;
  /** Categories flagged deductible (feeds itemized deductions in phase 7). */
  deductible: number;
}

function computeSpending(
  plan: Plan,
  year: number,
  yearsFromStart: number,
  generalInflation: number,
  anyoneWorking: boolean,
  primaryAge: number,
): SpendingBreakdown {
  const b = plan.budget;
  const inflationFactor = Math.pow(1 + generalInflation, yearsFromStart);
  const bandMult = ageBandMultiplier(plan, primaryAge);

  if (b.mode === 'detailed') {
    let essential = 0;
    let discretionary = 0;
    let deductible = 0;
    for (const cat of b.categories ?? []) {
      if (cat.frequency === 'one_time') {
        // One-time categories occur only in their start year.
        if (cat.startYear == null || year !== cat.startYear) continue;
      } else {
        if (cat.startYear != null && year < cat.startYear) continue;
        if (cat.endYear != null && year > cat.endYear) continue;
      }
      const perYear =
        cat.frequency === 'monthly' ? cat.amount * 12 :
        cat.frequency === 'quarterly' ? cat.amount * 4 :
        cat.amount;
      // Each category may inflate at its own rate (healthcare, tuition...).
      const rate = cat.inflationRateOverride ?? generalInflation;
      let amount = perYear * Math.pow(1 + rate, yearsFromStart);
      if (cat.tier === 'discretionary') {
        amount *= bandMult;
        discretionary += amount;
      } else {
        essential += amount;
      }
      if (cat.deductible) deductible += amount;
    }
    return { essential, discretionary, total: essential + discretionary, deductible };
  }

  let baseAnnual = 0;
  if (b.mode === 'simple') {
    baseAnnual = (b.annualAmount ?? 0) * inflationFactor;
    if (!anyoneWorking && b.retirementSpendingMultiplier != null) {
      baseAnnual *= b.retirementSpendingMultiplier;
    }
  } else {
    // staircase: dated step changes to the base level (today's dollars).
    let level = b.baseAnnualAmount ?? 0;
    for (const step of [...(b.steps ?? [])].sort((x, y) => x.startYear - y.startYear)) {
      if (year >= step.startYear) level = step.annualAmount;
    }
    baseAnnual = level * inflationFactor;
  }

  const essentialFraction = b.essentialFraction ?? 0.65;
  const essential = baseAnnual * essentialFraction;
  const discretionary = (baseAnnual - essential) * bandMult;
  return { essential, discretionary, total: essential + discretionary, deductible: 0 };
}
